import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, get, remove, update } from 'firebase/database';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import { FaSearch, FaFilter, FaTimes, FaChevronLeft, FaChevronRight, FaEdit, FaKey, FaDownload, FaGraduationCap, FaBriefcase, FaUser, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Image Modal Component for enlarging photos
const ImageModal = ({ isOpen, onClose, imageUrl, altText }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative flex flex-col items-center">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white text-4xl hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          ×
        </button>
        <img
          src={imageUrl}
          alt={altText}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

// List of parishes in Jamaica
const PARISHES = [
  "Clarendon", "Hanover", "Kingston", "Manchester", "Portland",
  "Saint Andrew", "Saint Ann", "Saint Catherine", "Saint Elizabeth",
  "Saint James", "Saint Mary", "Saint Thomas", "Trelawny", "Westmoreland"
];

// Employment types
const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" },
  { value: "internship", label: "Internship" },
  { value: "volunteer", label: "Volunteer" },
  { value: "freelancer", label: "Freelance" }
];

// Comprehensive list of industries
const INDUSTRIES_LIST = [
  "Information Technology", "Software Development", "Cybersecurity", "Data Science & Analytics",
  "Artificial Intelligence & Machine Learning", "Cloud Computing", "Web Development",
  "Mobile App Development", "IT Support & Services", "Telecommunications",
  "Banking & Financial Services", "Accounting & Auditing", "Insurance",
  "Investment & Asset Management", "Real Estate", "Consulting", "Human Resources",
  "Marketing & Advertising", "Public Relations", "Sales & Business Development",
  "Healthcare & Medical Services", "Nursing", "Pharmaceuticals", "Biotechnology",
  "Mental Health Services", "Dental Services", "Medical Research", "Health Administration",
  "Education & Teaching", "Higher Education", "Early Childhood Education",
  "Corporate Training", "E-Learning & EdTech", "Special Education",
  "Civil Engineering", "Mechanical Engineering", "Electrical Engineering",
  "Chemical Engineering", "Manufacturing", "Automotive", "Aerospace", "Construction",
  "Graphic Design", "Film & Video Production", "Photography", "Music & Audio",
  "Writing & Editing", "Journalism", "Broadcasting", "Gaming & Interactive Media",
  "Hotel & Resort Management", "Restaurant & Food Service", "Travel & Tourism",
  "Event Planning", "Cruise & Maritime",
  "Retail Management", "E-Commerce", "Customer Service", "Fashion & Apparel", "Beauty & Cosmetics",
  "Agriculture & Farming", "Environmental Services", "Renewable Energy",
  "Wildlife & Conservation", "Forestry", "Fishing & Aquaculture",
  "Legal Services", "Government & Public Administration", "Law Enforcement",
  "Military & Defense", "Non-Profit & NGO", "Social Services",
  "Logistics & Supply Chain", "Shipping & Freight", "Aviation",
  "Public Transportation", "Warehousing & Distribution",
  "Plumbing", "Electrical Work", "Carpentry", "HVAC", "Welding",
  "Masonry", "Auto Mechanics", "General Contracting",
  "Security Services", "Domestic Work & Housekeeping", "Childcare & Nanny Services",
  "Pet Care & Veterinary", "Fitness & Wellness", "Sports & Recreation",
  "Religious & Spiritual Services", "Other"
];

const AllCandidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    middleInitial: '',
    lastName: '',
    email: '',
    phone: '',
    parish: '',
    address: '',
    gender: '',
    employmentType: '',
    labourAvailability: '',
    birthDate: '',
    objectives: '',
    industriesOfInterest: [],
    isPublic: true,
    profileStatus: 'active'
  });
  const [isSaving, setIsSaving] = useState(false);

  // Industry selection state for edit modal
  const [editIndustrySearch, setEditIndustrySearch] = useState('');
  const [isEditIndustryDropdownOpen, setIsEditIndustryDropdownOpen] = useState(false);

  // Reset password state
  const [isResetting, setIsResetting] = useState(null);

  // Resend verification state
  const [isResendingVerification, setIsResendingVerification] = useState(null);

  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: '', alt: '' });

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    industry: '',
    gender: '',
    parish: '',
    employmentType: '',
    labourAvailability: ''
  });

  // Industry dropdown state
  const [industrySearchTerm, setIndustrySearchTerm] = useState('');
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);

  const db = getDatabase(app);
  const auth = getAuth(app);

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const candidatesRef = ref(db, 'candidates');
        const snapshot = await get(candidatesRef);

        if (snapshot.exists()) {
          const candidatesData = snapshot.val();
          const candidatesArray = Object.entries(candidatesData).map(([id, data]) => ({
            id,
            ...data,
            fullName: `${data.firstName} ${data.middleInitial ? data.middleInitial + ' ' : ''}${data.lastName}`,
            email: data.email,
            phone: data.phone,
            status: data.labourAvailability === 'yes' ? 'Available' : 'Unavailable'
          }));
          setCandidates(candidatesArray);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching candidates:', error);
        toast.error('Error loading candidates');
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [db]);

  // Filter industries based on search
  const filteredIndustries = INDUSTRIES_LIST.filter(industry =>
    industry.toLowerCase().includes(industrySearchTerm.toLowerCase())
  );

  // Get unique industries from candidates for the filter
  const availableIndustries = useMemo(() => {
    const industries = new Set();
    candidates.forEach(candidate => {
      if (candidate.industriesOfInterest && Array.isArray(candidate.industriesOfInterest)) {
        candidate.industriesOfInterest.forEach(ind => industries.add(ind));
      }
    });
    return Array.from(industries).sort();
  }, [candidates]);

  // Filter candidates based on search term and filters
  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      // Text search
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          candidate.fullName?.toLowerCase().includes(search) ||
          candidate.email?.toLowerCase().includes(search) ||
          candidate.phone?.toLowerCase().includes(search) ||
          candidate.parish?.toLowerCase().includes(search) ||
          candidate.address?.toLowerCase().includes(search) ||
          candidate.employmentType?.toLowerCase().includes(search) ||
          candidate.industriesOfInterest?.some(ind => ind.toLowerCase().includes(search));

        if (!matchesSearch) return false;
      }

      // Industry filter
      if (filters.industry) {
        const hasIndustry = candidate.industriesOfInterest?.includes(filters.industry);
        if (!hasIndustry) return false;
      }

      // Gender filter
      if (filters.gender) {
        if (candidate.gender?.toLowerCase() !== filters.gender.toLowerCase()) return false;
      }

      // Parish filter
      if (filters.parish) {
        const candidateParish = candidate.parish?.toLowerCase().replace(/st\./g, 'saint').replace(/\s+/g, ' ').trim();
        const filterParish = filters.parish.toLowerCase().replace(/st\./g, 'saint').replace(/\s+/g, ' ').trim();
        if (candidateParish !== filterParish) return false;
      }

      // Employment type filter
      if (filters.employmentType) {
        if (candidate.employmentType?.toLowerCase() !== filters.employmentType.toLowerCase()) return false;
      }

      // Labour availability filter
      if (filters.labourAvailability) {
        if (candidate.labourAvailability?.toLowerCase() !== filters.labourAvailability.toLowerCase()) return false;
      }

      return true;
    });
  }, [candidates, searchTerm, filters]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCandidates = filteredCandidates.slice(startIndex, endIndex);

  // Reset to first page when search/filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(paginatedCandidates.map(c => c.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (candidateId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
    } else {
      newSelected.add(candidateId);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = paginatedCandidates.length > 0 &&
    paginatedCandidates.every(c => selectedIds.has(c.id));

  const isSomeSelected = paginatedCandidates.some(c => selectedIds.has(c.id));

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      industry: '',
      gender: '',
      parish: '',
      employmentType: '',
      labourAvailability: ''
    });
    setSearchTerm('');
    setIndustrySearchTerm('');
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '') || searchTerm !== '';

  const handleDelete = async (candidateId) => {
    if (window.confirm('Are you sure you want to delete this candidate?')) {
      try {
        await remove(ref(db, `candidates/${candidateId}`));
        toast.success('Candidate deleted successfully');
        setCandidates(candidates.filter(c => c.id !== candidateId));
        const newSelected = new Set(selectedIds);
        newSelected.delete(candidateId);
        setSelectedIds(newSelected);
      } catch (error) {
        console.error('Error deleting candidate:', error);
        toast.error('Error deleting candidate');
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedIds.size} selected candidate(s)?`)) {
      try {
        const deletePromises = Array.from(selectedIds).map(id =>
          remove(ref(db, `candidates/${id}`))
        );
        await Promise.all(deletePromises);
        toast.success(`${selectedIds.size} candidate(s) deleted successfully`);
        setCandidates(candidates.filter(c => !selectedIds.has(c.id)));
        setSelectedIds(new Set());
      } catch (error) {
        console.error('Error deleting candidates:', error);
        toast.error('Error deleting candidates');
      }
    }
  };

  // Reset password handler
  const handleResetPassword = async (candidate) => {
    if (!candidate.email) {
      toast.error('No email address found for this candidate');
      return;
    }

    if (window.confirm(`Send password reset email to ${candidate.email}?`)) {
      setIsResetting(candidate.id);
      try {
        await sendPasswordResetEmail(auth, candidate.email);
        toast.success(`Password reset email sent to ${candidate.email}`);
      } catch (error) {
        console.error('Error sending password reset:', error);
        if (error.code === 'auth/user-not-found') {
          toast.error('No user found with this email address');
        } else {
          toast.error('Error sending password reset email');
        }
      } finally {
        setIsResetting(null);
      }
    }
  };

  // Resend verification email handler
  const handleResendVerification = async (candidate) => {
    if (!candidate.email) {
      toast.error('No email address found for this candidate');
      return;
    }

    if (candidate.emailVerified) {
      toast.info('This email is already verified');
      return;
    }

    if (window.confirm(`Send verification email to ${candidate.email}?`)) {
      setIsResendingVerification(candidate.id);
      try {
        const functions = getFunctions(app);
        const resendVerificationEmail = httpsCallable(functions, 'resendVerificationEmail');

        await resendVerificationEmail({
          userId: candidate.id,
          email: candidate.email,
          userType: 'candidate',
          userName: candidate.fullName
        });

        toast.success(`Verification email sent to ${candidate.email}`);
      } catch (error) {
        console.error('Error sending verification email:', error);
        if (error.code === 'functions/not-found') {
          toast.error('No user found with this email address in authentication');
        } else if (error.code === 'functions/permission-denied') {
          toast.error('Permission denied. Admin access required.');
        } else {
          toast.error(error.message || 'Error sending verification email');
        }
      } finally {
        setIsResendingVerification(null);
      }
    }
  };

  // Edit handlers
  const handleOpenEdit = (candidate) => {
    setEditFormData({
      firstName: candidate.firstName || '',
      middleInitial: candidate.middleInitial || '',
      lastName: candidate.lastName || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      parish: candidate.parish || '',
      address: candidate.address || '',
      gender: candidate.gender || '',
      employmentType: candidate.employmentType || '',
      labourAvailability: candidate.labourAvailability || '',
      birthDate: candidate.birthDate || '',
      objectives: candidate.objectives || '',
      industriesOfInterest: candidate.industriesOfInterest || [],
      isPublic: candidate.isPublic !== false,
      profileStatus: candidate.profileStatus || 'active'
    });
    setEditIndustrySearch('');
    setSelectedCandidate(candidate);
    setIsEditModalOpen(true);
  };

  // Handle opening image modal
  const handleOpenImage = (url, alt) => {
    setSelectedImage({ url, alt });
    setIsImageModalOpen(true);
  };

  // Toggle industry in edit form
  const toggleEditIndustry = (industry) => {
    setEditFormData(prev => {
      const isSelected = prev.industriesOfInterest.includes(industry);
      return {
        ...prev,
        industriesOfInterest: isSelected
          ? prev.industriesOfInterest.filter(i => i !== industry)
          : [...prev.industriesOfInterest, industry]
      };
    });
  };

  // Remove industry from edit form
  const removeEditIndustry = (industry) => {
    setEditFormData(prev => ({
      ...prev,
      industriesOfInterest: prev.industriesOfInterest.filter(i => i !== industry)
    }));
  };

  // Filter industries for edit modal dropdown
  const filteredEditIndustries = INDUSTRIES_LIST.filter(industry =>
    industry.toLowerCase().includes(editIndustrySearch.toLowerCase())
  );

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedCandidate) return;

    setIsSaving(true);
    try {
      const updates = {
        firstName: editFormData.firstName,
        middleInitial: editFormData.middleInitial,
        lastName: editFormData.lastName,
        email: editFormData.email,
        phone: editFormData.phone,
        parish: editFormData.parish,
        address: editFormData.address,
        gender: editFormData.gender,
        employmentType: editFormData.employmentType,
        labourAvailability: editFormData.labourAvailability,
        birthDate: editFormData.birthDate,
        objectives: editFormData.objectives,
        industriesOfInterest: editFormData.industriesOfInterest,
        isPublic: editFormData.isPublic,
        profileStatus: editFormData.profileStatus,
        updatedAt: new Date().toISOString()
      };

      await update(ref(db, `candidates/${selectedCandidate.id}`), updates);

      // Update local state
      setCandidates(candidates.map(c => {
        if (c.id === selectedCandidate.id) {
          return {
            ...c,
            ...updates,
            fullName: `${updates.firstName} ${updates.middleInitial ? updates.middleInitial + ' ' : ''}${updates.lastName}`,
            status: updates.labourAvailability === 'yes' ? 'Available' : 'Unavailable'
          };
        }
        return c;
      }));

      toast.success('Candidate updated successfully');
      setIsEditModalOpen(false);
      setSelectedCandidate(null);
    } catch (error) {
      console.error('Error updating candidate:', error);
      toast.error('Error updating candidate');
    } finally {
      setIsSaving(false);
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Format employment type for display
  const formatEmploymentType = (type) => {
    if (!type) return 'Not specified';
    const found = EMPLOYMENT_TYPES.find(t => t.value === type.toLowerCase());
    return found ? found.label : type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-blue-950 mb-4">All Candidates</h2>

        {/* Search Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by name, email, phone, location, industry..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-950 text-white border-blue-950'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FaFilter className="mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 bg-white text-blue-950 text-xs px-2 py-0.5 rounded-full">
                {Object.values(filters).filter(v => v !== '').length + (searchTerm ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4 border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-700">Filter Candidates</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-800 flex items-center"
                >
                  <FaTimes className="mr-1" /> Clear All Filters
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {/* Industry Filter */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search industries..."
                    value={filters.industry || industrySearchTerm}
                    onChange={(e) => {
                      setIndustrySearchTerm(e.target.value);
                      setIsIndustryDropdownOpen(true);
                      if (!e.target.value) {
                        handleFilterChange('industry', '');
                      }
                    }}
                    onFocus={() => setIsIndustryDropdownOpen(true)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                  {filters.industry && (
                    <button
                      onClick={() => {
                        handleFilterChange('industry', '');
                        setIndustrySearchTerm('');
                      }}
                      className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}

                  {isIndustryDropdownOpen && (
                    <>
                      <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {(industrySearchTerm ? filteredIndustries : availableIndustries).length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 text-sm">No industries found</div>
                        ) : (
                          (industrySearchTerm ? filteredIndustries : availableIndustries).map((industry) => (
                            <div
                              key={industry}
                              onClick={() => {
                                handleFilterChange('industry', industry);
                                setIndustrySearchTerm('');
                                setIsIndustryDropdownOpen(false);
                              }}
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${
                                filters.industry === industry ? 'bg-blue-50 text-blue-700' : ''
                              }`}
                            >
                              {industry}
                            </div>
                          ))
                        )}
                      </div>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsIndustryDropdownOpen(false)}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Parish Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location (Parish)</label>
                <select
                  value={filters.parish}
                  onChange={(e) => handleFilterChange('parish', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Parishes</option>
                  {PARISHES.map(parish => (
                    <option key={parish} value={parish}>{parish}</option>
                  ))}
                </select>
              </div>

              {/* Employment Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                <select
                  value={filters.employmentType}
                  onChange={(e) => handleFilterChange('employmentType', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Types</option>
                  {EMPLOYMENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Labour Availability Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Strenuous Activities</label>
                <select
                  value={filters.labourAvailability}
                  onChange={(e) => handleFilterChange('labourAvailability', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All</option>
                  <option value="yes">Available</option>
                  <option value="no">Not Available</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Results count and bulk actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
          <div className="text-sm text-gray-600">
            Showing {filteredCandidates.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredCandidates.length)} of {filteredCandidates.length} candidates
            {hasActiveFilters && ' (filtered)'}
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              Delete Selected ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={el => {
                    if (el) el.indeterminate = isSomeSelected && !isAllSelected;
                  }}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Photo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employment
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Industries
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Labour
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visibility
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedCandidates.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-6 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <FaSearch className="text-4xl text-gray-300 mb-2" />
                    <p>No candidates found matching your criteria</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedCandidates.map((candidate) => (
                <tr key={candidate.id} className={`hover:bg-gray-50 ${selectedIds.has(candidate.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(candidate.id)}
                      onChange={() => handleSelectOne(candidate.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {candidate.profile?.photo?.url ? (
                      <img
                        src={candidate.profile.photo.url}
                        alt={candidate.fullName}
                        className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity border-2 border-gray-200"
                        onClick={() => handleOpenImage(candidate.profile.photo.url, candidate.fullName)}
                        title="Click to enlarge"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <FaUser className="text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {candidate.fullName}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {candidate.gender || 'Not specified'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{candidate.email}</div>
                    <div className="flex items-center gap-1 mt-1">
                      {candidate.emailVerified ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <FaCheckCircle className="text-xs" /> Verified
                        </span>
                      ) : (
                        <span className="text-xs text-orange-600 flex items-center gap-1">
                          <FaEnvelope className="text-xs" /> Not Verified
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{candidate.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{candidate.parish || 'Not specified'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {formatEmploymentType(candidate.employmentType)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {candidate.industriesOfInterest && candidate.industriesOfInterest.length > 0 ? (
                        <>
                          {candidate.industriesOfInterest.slice(0, 2).map((ind, idx) => (
                            <span
                              key={idx}
                              className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded"
                            >
                              {ind}
                            </span>
                          ))}
                          {candidate.industriesOfInterest.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{candidate.industriesOfInterest.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      candidate.status === 'Available'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {candidate.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      candidate.isPublic !== false
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {candidate.isPublic !== false ? 'Public' : 'Private'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      candidate.profileStatus === 'active'
                        ? 'bg-blue-100 text-blue-800'
                        : candidate.profileStatus === 'inactive'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {candidate.profileStatus === 'active' ? 'Active' : candidate.profileStatus || 'Not set'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedCandidate(candidate);
                          setIsModalOpen(true);
                        }}
                        className="text-blue-950 hover:text-[#cddd3a]"
                        title="View"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleOpenEdit(candidate)}
                        className="text-green-600 hover:text-green-800"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleResetPassword(candidate)}
                        disabled={isResetting === candidate.id}
                        className="text-orange-600 hover:text-orange-800 disabled:opacity-50"
                        title="Reset Password"
                      >
                        {isResetting === candidate.id ? (
                          <span className="animate-spin">...</span>
                        ) : (
                          <FaKey />
                        )}
                      </button>
                      <button
                        onClick={() => handleResendVerification(candidate)}
                        disabled={isResendingVerification === candidate.id}
                        className={`disabled:opacity-50 ${
                          candidate.emailVerified
                            ? 'text-green-600 cursor-default'
                            : 'text-purple-600 hover:text-purple-800'
                        }`}
                        title={candidate.emailVerified ? 'Email Verified' : 'Resend Verification Email'}
                      >
                        {isResendingVerification === candidate.id ? (
                          <span className="animate-spin">...</span>
                        ) : candidate.emailVerified ? (
                          <FaCheckCircle />
                        ) : (
                          <FaEnvelope />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(candidate.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredCandidates.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                First
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <FaChevronRight />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isModalOpen && selectedCandidate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                {/* Header with Photo */}
                <div className="flex items-start gap-4 mb-4">
                  {selectedCandidate.profile?.photo?.url ? (
                    <img
                      src={selectedCandidate.profile.photo.url}
                      alt={selectedCandidate.fullName}
                      className="w-20 h-20 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity border-2 border-gray-200"
                      onClick={() => handleOpenImage(selectedCandidate.profile.photo.url, selectedCandidate.fullName)}
                      title="Click to enlarge"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                      <FaUser className="text-gray-400 text-2xl" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedCandidate.fullName}</h3>
                    <p className="text-sm text-gray-500 capitalize">{selectedCandidate.gender || 'Not specified'}</p>
                    <div className="flex gap-2 mt-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${selectedCandidate.isPublic !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {selectedCandidate.isPublic !== false ? 'Public' : 'Private'}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${selectedCandidate.profileStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {selectedCandidate.profileStatus === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t border-gray-200 py-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h4>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Email</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedCandidate.email || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Phone</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedCandidate.phone || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Address</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedCandidate.address || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Parish</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedCandidate.parish || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>

                {/* Personal Details */}
                <div className="border-t border-gray-200 py-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Personal Details</h4>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Birth Date</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedCandidate.birthDate ? new Date(selectedCandidate.birthDate).toLocaleDateString() : 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Employment Type</dt>
                      <dd className="mt-1 text-sm text-gray-900">{formatEmploymentType(selectedCandidate.employmentType)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Labour Availability</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedCandidate.labourAvailability === 'yes' ? 'Available for strenuous work' : 'Not available for strenuous work'}</dd>
                    </div>
                  </dl>
                </div>

                {/* Objectives */}
                {selectedCandidate.objectives && (
                  <div className="border-t border-gray-200 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Objectives</h4>
                    <p className="text-sm text-gray-900 whitespace-pre-line">{selectedCandidate.objectives}</p>
                  </div>
                )}

                {/* Industries of Interest */}
                {selectedCandidate.industriesOfInterest && selectedCandidate.industriesOfInterest.length > 0 && (
                  <div className="border-t border-gray-200 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Industries of Interest</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedCandidate.industriesOfInterest.map((industry, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skills */}
                {selectedCandidate.profile?.skills && selectedCandidate.profile.skills.length > 0 && (
                  <div className="border-t border-gray-200 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedCandidate.profile.skills.map((skill, index) => (
                        <span key={index} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Work Experience */}
                {selectedCandidate.profile?.workExperience && selectedCandidate.profile.workExperience.length > 0 && (
                  <div className="border-t border-gray-200 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <FaBriefcase className="mr-2" /> Work Experience
                    </h4>
                    <div className="space-y-3">
                      {selectedCandidate.profile.workExperience.map((exp, index) => (
                        <div key={index} className="border-l-2 border-blue-950 pl-3">
                          <p className="font-medium text-sm text-gray-900">{exp.position}</p>
                          <p className="text-sm text-gray-600">{exp.company}</p>
                          <p className="text-xs text-gray-500">{exp.startDate} - {exp.endDate || 'Present'}</p>
                          {exp.description && <p className="text-xs text-gray-600 mt-1">{exp.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {selectedCandidate.profile?.education && selectedCandidate.profile.education.length > 0 && (
                  <div className="border-t border-gray-200 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <FaGraduationCap className="mr-2" /> Education
                    </h4>
                    <div className="space-y-3">
                      {selectedCandidate.profile.education.map((edu, index) => (
                        <div key={index} className="border-l-2 border-blue-950 pl-3">
                          <p className="font-medium text-sm text-gray-900">{edu.degree} {edu.field && `in ${edu.field}`}</p>
                          <p className="text-sm text-gray-600">{edu.institution}</p>
                          <p className="text-xs text-gray-500">
                            {edu.startDate && `${edu.startDate} - ${edu.endDate || 'Present'}`}
                            {edu.graduationYear && !edu.startDate && `Class of ${edu.graduationYear}`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resume */}
                {selectedCandidate.profile?.resume?.url && (
                  <div className="border-t border-gray-200 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Resume</h4>
                    <a
                      href={selectedCandidate.profile.resume.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-950 text-white text-sm rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
                    >
                      <FaDownload className="mr-2" /> Download Resume
                    </a>
                  </div>
                )}

                {/* Timestamps */}
                <div className="border-t border-gray-200 py-4 text-xs text-gray-500">
                  <p>Created: {selectedCandidate.createdAt ? new Date(selectedCandidate.createdAt).toLocaleString() : 'N/A'}</p>
                  <p>Last Updated: {selectedCandidate.updatedAt ? new Date(selectedCandidate.updatedAt).toLocaleString() : 'N/A'}</p>
                  <p>Last Login: {selectedCandidate.lastLoginAt ? new Date(selectedCandidate.lastLoginAt).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-950 text-base font-medium text-white hover:bg-[#cddd3a] hover:text-blue-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    handleOpenEdit(selectedCandidate);
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm mt-2 sm:mt-0"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedCandidate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSaveEdit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[70vh] overflow-y-auto">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Edit Candidate
                  </h3>

                  {/* Photo Preview */}
                  {selectedCandidate.profile?.photo?.url && (
                    <div className="mb-4 flex items-center gap-4">
                      <img
                        src={selectedCandidate.profile.photo.url}
                        alt={selectedCandidate.fullName}
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                      />
                      <p className="text-sm text-gray-500">Profile photo (managed by candidate)</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Personal Info Section */}
                    <div className="border-b pb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h4>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                          <input
                            type="text"
                            name="firstName"
                            value={editFormData.firstName}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">M.I.</label>
                          <input
                            type="text"
                            name="middleInitial"
                            value={editFormData.middleInitial}
                            onChange={handleEditChange}
                            maxLength={1}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                          <input
                            type="text"
                            name="lastName"
                            value={editFormData.lastName}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                          <select
                            name="gender"
                            value={editFormData.gender}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                            <option value="prefer-not-to-say">Prefer not to say</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date</label>
                          <input
                            type="date"
                            name="birthDate"
                            value={editFormData.birthDate}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Section */}
                    <div className="border-b pb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h4>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            name="email"
                            value={editFormData.email}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">Note: Does not change login credentials</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            name="phone"
                            value={editFormData.phone}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                          <input
                            type="text"
                            name="address"
                            value={editFormData.address}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Parish</label>
                          <select
                            name="parish"
                            value={editFormData.parish}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Parish</option>
                            {PARISHES.map(parish => (
                              <option key={parish} value={parish}>{parish}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Employment Section */}
                    <div className="border-b pb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Employment Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                          <select
                            name="employmentType"
                            value={editFormData.employmentType}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Type</option>
                            {EMPLOYMENT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Strenuous Activities</label>
                          <select
                            name="labourAvailability"
                            value={editFormData.labourAvailability}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select</option>
                            <option value="yes">Available</option>
                            <option value="no">Not Available</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Objectives */}
                    <div className="border-b pb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Objectives</h4>
                      <textarea
                        name="objectives"
                        value={editFormData.objectives}
                        onChange={handleEditChange}
                        rows={3}
                        placeholder="Career objectives and goals..."
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Industries of Interest */}
                    <div className="border-b pb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Industries of Interest</h4>
                      {editFormData.industriesOfInterest.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {editFormData.industriesOfInterest.map((industry) => (
                            <span
                              key={industry}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                            >
                              {industry}
                              <button
                                type="button"
                                onClick={() => removeEditIndustry(industry)}
                                className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search and select industries..."
                          value={editIndustrySearch}
                          onChange={(e) => {
                            setEditIndustrySearch(e.target.value);
                            setIsEditIndustryDropdownOpen(true);
                          }}
                          onFocus={() => setIsEditIndustryDropdownOpen(true)}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {isEditIndustryDropdownOpen && (
                          <>
                            <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {filteredEditIndustries.length === 0 ? (
                                <div className="px-3 py-2 text-gray-500 text-sm">No industries found</div>
                              ) : (
                                filteredEditIndustries.slice(0, 20).map((industry) => (
                                  <div
                                    key={industry}
                                    onClick={() => {
                                      toggleEditIndustry(industry);
                                      setEditIndustrySearch('');
                                    }}
                                    className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center justify-between ${
                                      editFormData.industriesOfInterest.includes(industry) ? 'bg-blue-50 text-blue-700' : ''
                                    }`}
                                  >
                                    <span>{industry}</span>
                                    {editFormData.industriesOfInterest.includes(industry) && (
                                      <span className="text-blue-600">✓</span>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setIsEditIndustryDropdownOpen(false)}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {/* Profile Status Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Profile Settings</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Profile Visibility</label>
                          <select
                            name="isPublic"
                            value={editFormData.isPublic ? 'true' : 'false'}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, isPublic: e.target.value === 'true' }))}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="true">Public</option>
                            <option value="false">Private</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Profile Status</label>
                          <select
                            name="profileStatus"
                            value={editFormData.profileStatus}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-950 text-base font-medium text-white hover:bg-[#cddd3a] hover:text-blue-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedCandidate(null);
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm mt-2 sm:mt-0"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal for enlarged profile photo */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={selectedImage.url}
        altText={selectedImage.alt}
      />
    </div>
  );
};

export default AllCandidates;
